import React, { useState, useEffect } from "react";
import {
  PencilIcon,
  CheckIcon,
  XIcon,
  TrashIcon,
  PlusIcon,
} from "lucide-react";
import { db } from "../../config/firebaseConfig";
import { ref, set, get } from "firebase/database";
import { useToast } from "../../@/components/ui/use-toast";
import SortupDialog from "../../components/common/SortupDialog";
import LayoutSkeleton from "../../components/skeletons/LayoutSkeleton";

interface ImprintSection {
  title: string;
  content: string;
  listItems?: string[];
}

interface ImprintData {
  introduction: string;
  sections: ImprintSection[];
}

const EditImprintandData = () => {
  // State for storing imprint data
  const [imprintData, setImprintData] = useState<ImprintData>({
    introduction: "",
    sections: [],
  });

  // State for editing introduction
  const [isEditingIntro, setIsEditingIntro] = useState(false);
  const [editIntroText, setEditIntroText] = useState("");

  // State for editing section
  const [editingSectionIndex, setEditingSectionIndex] = useState<number | null>(
    null
  );
  const [editSectionTitle, setEditSectionTitle] = useState("");
  const [editSectionContent, setEditSectionContent] = useState("");

  // State for editing list items
  const [editingListItems, setEditingListItems] = useState<string[]>([]);

  // State for adding new section
  const [isAddingSection, setIsAddingSection] = useState(false);

  // State for adding new list item
  const [newListItem, setNewListItem] = useState("");

  // Loading state
  const [loading, setLoading] = useState(true);

  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<number | null>(null);

  useEffect(() => {
    // Fetch imprint data from Firebase
    const fetchImprintData = async () => {
      try {
        const imprintRef = ref(db, "LayoutSettingData/ImprintData");
        const snapshot = await get(imprintRef);

        if (snapshot.exists()) {
          setImprintData(snapshot.val());
        } else {
          // If no data exists, use default content
          const defaultData: ImprintData = {
            introduction:
              "At Afrocart, your privacy and the protection of your data are of utmost importance. This section provides key information about our company, how we manage your data, and your rights under data protection laws.",
            sections: [
              {
                title: "Company Information",
                content: "",
                listItems: [
                  "Company Name: Afrocart Inc.",
                  "Customer Support: +1289-460-1541",
                  "Inquiries: admin@afrocart.ca",
                ],
              },
              {
                title: "Data Protection Officer (DPO)",
                content:
                  "If you have any concerns or questions related to data privacy or how your personal information is handled, please contact our Data Protection Officer at dpo@afrocart.ca.",
              },
              {
                title: "Data Usage and Privacy",
                content:
                  "We are committed to using your personal information only in compliance with relevant legal standards. This includes processing data for improving our services, facilitating transactions, and enhancing your overall experience on our platform. For more detailed insights into our data practices, please review our Privacy Policy.",
              },
              {
                title: "Your Rights Under Data Protection Laws",
                content:
                  "You have the right to access, correct, or delete your personal data at any time. Additionally, you can object to how your data is processed or request limitations on its use. To exercise these rights or if you need further details, please reach out to our Data Protection Officer or refer to our Privacy Policy.",
              },
              {
                title: "Legal Updates",
                content:
                  "We regularly review and update our data protection practices to ensure that they remain in full compliance with evolving laws and regulations. Please check back here for the latest updates on how we handle your personal information.",
              },
            ],
          };
          setImprintData(defaultData);
          await saveToFirebase(defaultData);
        }
      } catch (error) {
        console.error("Error fetching imprint data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchImprintData();
  }, []);

  // Save data to Firebase
  const saveToFirebase = async (data = imprintData) => {
    try {
      const imprintRef = ref(db, "LayoutSettingData/ImprintData");
      await set(imprintRef, data);
      return true;
    } catch (error) {
      console.error("Error saving imprint data:", error);
      return false;
    }
  };

  // Handle editing introduction
  const handleEditIntro = () => {
    setEditIntroText(imprintData.introduction);
    setIsEditingIntro(true);
  };

  const handleSaveIntro = async () => {
    const updatedData = { ...imprintData, introduction: editIntroText };
    const success = await saveToFirebase(updatedData);
    if (success) {
      setImprintData(updatedData);
      setIsEditingIntro(false);
      toast({
        title: "Success",
        description: "Introduction updated successfully",
        showIcon: true,
        variant: "success",
      });
    }
  };

  // Handle editing section
  const handleEditSection = (index: number) => {
    const section = imprintData.sections[index];
    setEditingSectionIndex(index);
    setEditSectionTitle(section.title);
    setEditSectionContent(section.content);
    setEditingListItems(section.listItems || []);
  };

  const handleSaveSection = async () => {
    if (editingSectionIndex !== null) {
      const updatedSections = [...imprintData.sections];
      updatedSections[editingSectionIndex] = {
        title: editSectionTitle,
        content: editSectionContent,
        listItems: editingListItems.length > 0 ? editingListItems : undefined,
      };

      const updatedData = {
        ...imprintData,
        sections: updatedSections,
      };

      const success = await saveToFirebase(updatedData);
      if (success) {
        setImprintData(updatedData);
        setEditingSectionIndex(null);
        toast({
          title: "Success",
          description: "Section updated successfully",
          showIcon: true,
          variant: "success",
        });
      }
    }
  };

  // Handle adding new section
  const handleAddSection = () => {
    setEditSectionTitle("");
    setEditSectionContent("");
    setEditingListItems([]);
    setIsAddingSection(true);
  };

  const handleSaveNewSection = async () => {
    if (editSectionTitle.trim() === "" || editSectionContent.trim() === "") {
      toast({
        title: "Error",
        description: "Please enter both title and content for the new section",
        variant: "destructive",
        showIcon: true,
      });
      return;
    }

    const newSection: ImprintSection = {
      title: editSectionTitle,
      content: editSectionContent,
    };

    if (editingListItems.length > 0) {
      newSection.listItems = editingListItems;
    }

    const updatedData = {
      ...imprintData,
      sections: [...imprintData.sections, newSection],
    };

    const success = await saveToFirebase(updatedData);
    if (success) {
      setImprintData(updatedData);
      setIsAddingSection(false);
      toast({
        title: "Success",
        description: "New section added successfully",
        variant: "success",
        showIcon: true,
      });
    } else {
      toast({
        title: "Error",
        description: "Failed to add new section",
        variant: "destructive",
        showIcon: true,
      });
    }
  };

  // Handle deleting section
  const handleDeleteSection = async (index: number) => {
    setSectionToDelete(index);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (sectionToDelete !== null) {
      const updatedSections = imprintData.sections.filter(
        (_, i) => i !== sectionToDelete
      );

      const updatedData = {
        ...imprintData,
        sections: updatedSections,
      };

      const success = await saveToFirebase(updatedData);
      if (success) {
        setImprintData(updatedData);
        toast({
          title: "Success",
          description: "Section deleted successfully",
          variant: "success",
          showIcon: true,
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to delete section",
          variant: "destructive",
          showIcon: true,
        });
      }
    }
    setShowDeleteDialog(false);
    setSectionToDelete(null);
  };

  // Handle adding list item
  const handleAddListItem = () => {
    if (newListItem.trim()) {
      setEditingListItems([...editingListItems, newListItem]);
      setNewListItem("");
    }
  };

  // Handle deleting list item
  const handleDeleteListItem = (index: number) => {
    const updatedItems = editingListItems.filter((_, i) => i !== index);
    setEditingListItems(updatedItems);
  };

  // Handle cancel button click
  const handleCancel = () => {
    setIsEditingIntro(false);
    setEditingSectionIndex(null);
    setIsAddingSection(false);
  };

  // Render introduction section
  const renderIntroduction = () => {
    return (
      <div className="w-full">
        {isEditingIntro ? (
          <div className="mt-3">
            <textarea
              value={editIntroText}
              onChange={(e) => setEditIntroText(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md min-h-[200px] focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
            />
            <div className="flex mt-3 space-x-3">
              <button
                onClick={handleSaveIntro}
                className="bg-customGreenColor hover:bg-opacity-90 transition-all text-white px-4 py-2 rounded-md flex items-center"
              >
                <CheckIcon className="h-4 w-4 mr-2" /> Save
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-200 hover:bg-gray-300 transition-all text-gray-700 px-4 py-2 rounded-md flex items-center"
              >
                <XIcon className="h-4 w-4 mr-2" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-start">
            <div className="flex-grow">
              <p className="leading-relaxed">{imprintData.introduction}</p>
            </div>
            <button
              className="text-customGreenColor hover:text-opacity-80 transition-all ml-3 flex-shrink-0"
              onClick={handleEditIntro}
            >
              <PencilIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // Render section
  const renderSection = (section: ImprintSection, index: number) => {
    return (
      <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
        {editingSectionIndex === index ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Section Title
              </label>
              <input
                type="text"
                value={editSectionTitle}
                onChange={(e) => setEditSectionTitle(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Section Content
              </label>
              <textarea
                value={editSectionContent}
                onChange={(e) => setEditSectionContent(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded min-h-[100px]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                List Items (if applicable)
              </label>
              <div className="space-y-2">
                {editingListItems.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex items-center">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => {
                        const updatedItems = [...editingListItems];
                        updatedItems[itemIndex] = e.target.value;
                        setEditingListItems(updatedItems);
                      }}
                      className="flex-1 p-2 border border-gray-300 rounded"
                    />
                    <button
                      className="ml-2 text-red-500"
                      onClick={() => handleDeleteListItem(itemIndex)}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
                <div className="flex items-center">
                  <input
                    type="text"
                    value={newListItem}
                    onChange={(e) => setNewListItem(e.target.value)}
                    placeholder="Add new list item"
                    className="flex-1 p-2 border border-gray-300 rounded"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleAddListItem();
                      }
                    }}
                  />
                  <button
                    className="ml-2 bg-gray-200 p-2 rounded"
                    onClick={handleAddListItem}
                  >
                    <PlusIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleSaveSection}
                className="bg-customGreenColor text-white px-3 py-1 rounded flex items-center"
              >
                <CheckIcon className="h-4 w-4 mr-1" /> Save
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-300 text-gray-700 px-3 py-1 rounded flex items-center"
              >
                <XIcon className="h-4 w-4 mr-1" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-between">
              <h3 className="font-helveticaBold text-black text-lg">
                {section.title}
              </h3>
              <div className="flex space-x-2">
                <button
                  className="text-customGreenColor"
                  onClick={() => handleEditSection(index)}
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  className="text-red-500"
                  onClick={() => handleDeleteSection(index)}
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <p className="mt-2 font-helvetica">{section.content}</p>
            {section.listItems && section.listItems.length > 0 && (
              <ul className="list-disc pl-5 mt-2">
                {section.listItems.map((item, itemIndex) => (
                  <li key={itemIndex} className="font-helvetica">
                    {item}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return <LayoutSkeleton />;
  }

  return (
    <div className="max-w-[1400px] mx-auto bg-white p-6">
      <div className="w-full flex flex-col font-normal text-sm space-y-6 text-black">
        <div className="flex gap-4 flex-col md:flex-row justify-between items-center w-full mb-8">
          <h1 className="font-helveticaBold text-3xl md:text-4xl text-black">
            Imprint & Data Protection
          </h1>
          <button
            className="text-white w-full md:w-auto bg-customGreenColor justify-center hover:bg-opacity-90 transition-all px-4 py-2 rounded-md flex items-center"
            onClick={handleAddSection}
          >
            <PlusIcon className="h-4 w-4 mr-2" /> Add Section
          </button>
        </div>

        {/* Introduction section */}
        <div className="bg-gray-50 rounded-lg p-6 shadow-sm">
          {renderIntroduction()}
        </div>

        {/* Add New Section Form */}
        {isAddingSection && (
          <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
            <h3 className="font-helveticaBold text-black text-xl mb-4">
              Add New Section
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Section Title
                </label>
                <input
                  type="text"
                  value={editSectionTitle}
                  onChange={(e) => setEditSectionTitle(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded"
                  placeholder="Enter section title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Section Content
                </label>
                <textarea
                  value={editSectionContent}
                  onChange={(e) => setEditSectionContent(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded min-h-[100px]"
                  placeholder="Enter section content"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  List Items (if applicable)
                </label>
                <div className="space-y-2">
                  {editingListItems.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-center">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => {
                          const updatedItems = [...editingListItems];
                          updatedItems[itemIndex] = e.target.value;
                          setEditingListItems(updatedItems);
                        }}
                        className="flex-1 p-2 border border-gray-300 rounded"
                      />
                      <button
                        className="ml-2 text-red-500"
                        onClick={() => handleDeleteListItem(itemIndex)}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                  <div className="flex items-center">
                    <input
                      type="text"
                      value={newListItem}
                      onChange={(e) => setNewListItem(e.target.value)}
                      placeholder="Add new list item"
                      className="flex-1 p-2 border border-gray-300 rounded"
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleAddListItem();
                        }
                      }}
                    />
                    <button
                      className="ml-2 bg-gray-200 p-2 rounded"
                      onClick={handleAddListItem}
                    >
                      <PlusIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleSaveNewSection}
                  className="bg-customGreenColor text-white px-3 py-1 rounded flex items-center"
                >
                  <CheckIcon className="h-4 w-4 mr-1" /> Save
                </button>
                <button
                  onClick={handleCancel}
                  className="bg-gray-300 text-gray-700 px-3 py-1 rounded flex items-center"
                >
                  <XIcon className="h-4 w-4 mr-1" /> Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sections List */}
        <div className="grid gap-6">
          {imprintData.sections.map((section, index) =>
            renderSection(section, index)
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <SortupDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSectionToDelete(null);
        }}
        header="Confirm Delete"
        footerButtons={[
          {
            name: "Delete",
            onClick: confirmDelete,
            disabled: false,
            className: "bg-customGreenColor text-white hover:bg-opacity-90",
          },
          {
            name: "Cancel",
            onClick: () => {
              setShowDeleteDialog(false);
              setSectionToDelete(null);
            },
            disabled: false,
            className: "bg-gray-200 text-gray-700 hover:bg-gray-300",
          },
        ]}
      >
        <p>
          Are you sure you want to delete this section? This action cannot be
          undone.
        </p>
      </SortupDialog>
    </div>
  );
};

export default EditImprintandData;
